import { Zalo } from '@teko/teko-ecommerce';
import { ScrollTopArrow, styled } from '@teko/ui';
import { useTenantConfig } from 'hooks';
import dynamic from 'next/dynamic';
import { useEffect, useMemo, useState } from 'react';
import { ICON_DISPLAY_PRIORITY } from 'types';
import { MessengerIcon } from './MessengerIcon';
import { PhoneIcon } from './PhoneIcon';
import { ZaloIcon } from './ZaloIcon';
import { ZALO_LIFT_REM, ZaloOASlot } from './ZaloOASlot';

const ChatbotButton = dynamic(() =>
  import('./ChatbotButton').then((mod) => mod.ChatbotButton),
);

const ChatbotLauncher = dynamic(
  () =>
    import('modules/chatbot/components/ChatbotLauncher').then(
      (mod) => mod.ChatbotLauncher,
    ),
  { ssr: false },
);

const DEFAULT_FLOATING_ICONS_DISPLAY_PRIORITY = [
  ICON_DISPLAY_PRIORITY.SCROLL_TOP,
  ICON_DISPLAY_PRIORITY.PHONE,
  ICON_DISPLAY_PRIORITY.MESSENGER,
  ICON_DISPLAY_PRIORITY.ZALO,
  ICON_DISPLAY_PRIORITY.CHATBOT_AI,
  ICON_DISPLAY_PRIORITY.ZALO_OA,
];

export const FloatingIconList = () => {
  const {
    contactLink,
    isDisplayMessenger,
    zaloContactLink,
    displayHotlineFloatIcon,
    clientConsultantPhone,
    floatingIconsDisplayPriority,
    renderFloatingIconsDynamically,
    terminalId,
    currentLanguage,
    chatbotAgentCode,
    chatbotConfig,
    hotlineFloatIconCustomImage,
  } = useTenantConfig();
  const displayPriority = useMemo(() => {
    return !!floatingIconsDisplayPriority?.length
      ? floatingIconsDisplayPriority
      : DEFAULT_FLOATING_ICONS_DISPLAY_PRIORITY;
  }, [floatingIconsDisplayPriority]);

  const [zaloWidgetPresent, setZaloWidgetPresent] = useState(false);
  useEffect(() => {
    let raf = 0;
    const check = () => {
      raf = 0;
      setZaloWidgetPresent(
        !!document.querySelector('.zalo-chat-widget iframe'),
      );
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(check);
    };
    check();
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      if (raf) cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, []);
  const zaloLift = zaloWidgetPresent ? ZALO_LIFT_REM : 0;

  if (
    !zaloWidgetPresent &&
    !zaloContactLink &&
    (!contactLink || !isDisplayMessenger) &&
    (!displayHotlineFloatIcon || !clientConsultantPhone) &&
    !displayPriority?.includes(ICON_DISPLAY_PRIORITY.CHATBOT) &&
    (!chatbotConfig ||
      !displayPriority?.includes(ICON_DISPLAY_PRIORITY.CHATBOT_AI))
  ) {
    return null;
  }

  return !!renderFloatingIconsDynamically ? (
    <IconListWrapper>
      {displayPriority.map((iconPriority) => {
        switch (iconPriority) {
          case ICON_DISPLAY_PRIORITY.SCROLL_TOP:
            return (
              <ScrollTopArrow
                position={{ bottom: undefined, right: undefined }}
                displayPosition="relative"
                key={iconPriority}
              />
            );
          case ICON_DISPLAY_PRIORITY.PHONE:
            return (
              !!clientConsultantPhone &&
              !!displayHotlineFloatIcon && (
                <PhoneIcon
                  consultantPhone={clientConsultantPhone}
                  customImage={hotlineFloatIconCustomImage}
                  key={iconPriority}
                />
              )
            );
          case ICON_DISPLAY_PRIORITY.ZALO:
            return (
              !!zaloContactLink && (
                <ZaloIcon url={zaloContactLink} key={iconPriority} />
              )
            );
          case ICON_DISPLAY_PRIORITY.MESSENGER:
            return (
              !!contactLink &&
              !!isDisplayMessenger && (
                <MessengerIcon
                  url={contactLink}
                  key={iconPriority}
                  responsiveIcon
                />
              )
            );
          case ICON_DISPLAY_PRIORITY.CHATBOT:
            return (
              <ChatbotButton
                key={iconPriority}
                zaloContactLink={zaloContactLink}
                terminalId={terminalId}
                currentLng={currentLanguage}
                chatbotAgentCode={chatbotAgentCode}
              />
            );
          case ICON_DISPLAY_PRIORITY.CHATBOT_AI:
            return !!chatbotConfig && <ChatbotLauncher key={iconPriority} />;
          case ICON_DISPLAY_PRIORITY.ZALO_OA:
            return (
              <ZaloOASlot
                key={iconPriority}
                variant="column"
                present={zaloWidgetPresent}
              />
            );
        }
      })}
    </IconListWrapper>
  ) : (
    <>
      <ScrollTopArrow
        position={{ bottom: 13 + zaloLift, right: zaloWidgetPresent ? 2.5 : 3 }}
      />
      {clientConsultantPhone && displayHotlineFloatIcon && (
        <PhoneIcon
          consultantPhone={clientConsultantPhone}
          customImage={hotlineFloatIconCustomImage}
          position="fixed"
          right={zaloWidgetPresent ? 2.5 : 3.125}
          bottom={9.25 + zaloLift}
        />
      )}
      {contactLink && isDisplayMessenger && (
        <MessengerIcon
          url={contactLink}
          position="fixed"
          right={2.5}
          bottom={5.6 + zaloLift}
        />
      )}
      {zaloContactLink && <Zalo url={zaloContactLink} />}
      {chatbotConfig && (
        <ChatbotLauncher position="fixed" right={2.5} bottom={2 + zaloLift} />
      )}
      <ZaloOASlot
        variant="fixed"
        present={zaloWidgetPresent}
        bottom={2}
        right={2.5}
      />
    </>
  );
};

const IconListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: end;
  gap: 0.5rem;
  position: fixed;
  bottom: 2rem;
  right: 2.5rem;
  background-color: transparent;
  border: none;
  width: fit-content;
  z-index: 1030;
  @media print {
    display: none;
  }
  .fb-chat {
    margin: -8px -8px 0 0;
  }
`;
